import { mapFocusSearch } from "#app/lib/map";
import type { EntityViewRow } from "#app/lib/data";
import type { EntityViewsScope } from "#app/lib/hooks/use-entity-views";
import { viewPath } from "#app/lib/views/view-routes";

/** The app's origin; empty where there is no window (SSR, a node test). The
 *  one reader of `window.location.origin`: every absolute app URL, and every
 *  URL parsed against the app, starts here. */
export function appOrigin(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

/** An absolute URL for an app route that already carries its workspace
 *  segment (a stored dashboard route). */
export function routeHref(route: string): string {
  return `${appOrigin()}${route}`;
}

/** An absolute URL for `path` under the workspace's slug. `path` starts with
 *  a slash. This is the one place the app composes a shareable link. */
export function workspaceHref(workspace: string, path: string): string {
  return `${appOrigin()}/${workspace}${path}`;
}

/** The shareable URL for an issue: its workflow board deep-linked to the issue
 *  overlay. Same shape global search builds for its result links. */
export function issueHref(
  workspace: string,
  workflowId: string,
  issueId: string,
): string {
  return workspaceHref(workspace, `/workflow/${workflowId}?issue=${issueId}`);
}

/** The shareable URL for a workflow: `/workflow/:id` lands on its default view. */
export function workflowHref(workspace: string, workflowId: string): string {
  return workspaceHref(workspace, `/workflow/${workflowId}`);
}

/** The shareable URL for a team's page. */
export function teamHref(workspace: string, teamId: string): string {
  return workspaceHref(workspace, `/team/${teamId}`);
}

/** The shareable URL for a saved view in its scope (a workflow's or team's
 *  tab, a workspace dashboard). */
export function viewHref(
  workspace: string,
  scope: EntityViewsScope,
  view: EntityViewRow,
): string {
  return workspaceHref(workspace, viewPath(scope, view));
}

/** The shareable URL for a place on the map: the Live Map focused on the
 *  coordinates (and address, when known) through the route's one-shot focus
 *  params — the same grammar `useAppNavigate` pushes for an in-app pan. */
export function mapPlaceHref(
  workspace: string,
  place: { lat: number; lng: number; addressId?: string | null | undefined },
): string {
  return workspaceHref(workspace, `/map?${mapFocusSearch(place)}`);
}
